import {
  type DestroyOptions,
  Container,
  FederatedPointerEvent,
  Graphics,
  Matrix,
  Point,
  Rectangle
} from 'pixi.js';

/**
 * 控制点标识。
 * - 四角 / 四边中点用于缩放（resize）
 * - rotater 用于旋转（rotate）
 */
export type TransformerAnchor =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'middle-right'
  | 'bottom-right'
  | 'bottom-center'
  | 'bottom-left'
  | 'middle-left'
  | 'rotater';

/**
 * 变形框（Box）的数据结构。
 * x/y 为左上角顶点坐标，width/height 为边长（可带符号，负值表示翻转），
 * rotation 为框的旋转角度（绕左上角顶点）。
 *
 * ⚠️ 坐标空间：本类内部 this.box 始终存储在「世界坐标系」下，
 * 这样移动 / 缩放 / 旋转的数学计算能与目标节点自身的 worldTransform 自洽。
 * 仅在绘制（redraw）时才把它换算回 transformer 自身的局部坐标系。
 */
export interface TransformerBox {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

/** PixiTransformer 的可配置项 */
export interface PixiTransformerOptions {
  /** 绑定的目标节点列表 */
  nodes?: Container[];
  /** 启用的控制点（默认八方向缩放点） */
  enabledAnchors?: TransformerAnchor[];
  /** 是否允许缩放 */
  resizeEnabled?: boolean;
  /** 是否允许旋转 */
  rotateEnabled?: boolean;
  /** 旋转控制点距离上边线的偏移量 */
  rotateAnchorOffset?: number;
  /** 框与目标之间的内边距 */
  padding?: number;
  /** 控制点尺寸 */
  anchorSize?: number;
  /** 控制点圆角半径 */
  anchorCornerRadius?: number;
  /** 控制点填充色 */
  anchorFill?: number;
  /** 控制点描边色（支持 hex 数字或 CSS 颜色字符串，如 'rgba(0,0,0,0.15)'） */
  anchorStroke?: number | string;
  /** 控制点描边宽度 */
  anchorStrokeWidth?: number;
  /** 边框描边色 */
  borderStroke?: number;
  /** 边框描边宽度 */
  borderStrokeWidth?: number;
  /** 缩放时是否锁定宽高比 */
  keepRatio?: boolean;
  /** 是否以中心为基准缩放 */
  centeredScaling?: boolean;
  /** 是否允许翻转（宽高变负） */
  flipEnabled?: boolean;
  /** 单目标时是否沿用目标自身的旋转 */
  useSingleNodeRotation?: boolean;
  /** 约束函数：可在外部限制 box 的最终范围 */
  boundBoxFunc?: (oldBox: TransformerBox, newBox: TransformerBox) => TransformerBox;
  /** 拖拽过程中每帧触发（targets 为当前目标节点列表的拷贝，mode 为当前拖拽模式），可用于实时显示尺寸等轻量场景 */
  onChange?: (targets: Container[], mode: DragMode) => void;
  /** 拖拽结束（抬手）时触发（参数为当前目标节点列表的拷贝），用于提交数据，如调用 element.syncAttrsFromView() */
  onTransformEnd?: (targets: Container[]) => void;
}

/** 拖拽模式：移动 / 缩放 / 旋转 */
type DragMode = 'move' | 'resize' | 'rotate';

/** 一次拖拽过程中的快照状态，用于增量计算 */
interface DragState {
  mode: DragMode;
  anchor?: TransformerAnchor;
  /** 指针按下时的世界坐标 */
  pointerStart: Point;
  /** 拖拽开始时的 box（世界坐标） */
  startBox: TransformerBox;
  /** 拖拽开始时去掉 padding 后的内容 box（世界坐标） */
  startContentBox: TransformerBox;
  /** 拖拽开始时各目标的世界变换矩阵 */
  startWorldMatrices: Matrix[];
  /** 拖拽开始时各目标的位置（用于 move 模式增量） */
  startPositions: Point[];
}

/** 默认启用的八方向缩放控制点 */
export const DEFAULT_ANCHORS: TransformerAnchor[] = [
  'top-left',
  'top-center',
  'top-right',
  'middle-right',
  'bottom-right',
  'bottom-center',
  'bottom-left',
  'middle-left'
];

/** 各控制点对应的鼠标光标 */
const CURSORS: Record<TransformerAnchor, string> = {
  'top-left': 'nwse-resize',
  'top-center': 'ns-resize',
  'top-right': 'nesw-resize',
  'middle-right': 'ew-resize',
  'bottom-right': 'nwse-resize',
  'bottom-center': 'ns-resize',
  'bottom-left': 'nesw-resize',
  'middle-left': 'ew-resize',
  rotater: 'grab'
};

/** 浮点比较用的极小值，避免除零 / 退化情况 */
const EPSILON = 0.000001;

/**
 * PixiJS 版本的「变形框 / Transformer」。
 *
 * 行为类似 Konva.Transformer：给一组目标节点套上一个可移动、缩放、旋转的边框，
 * 操作边框即反向修改目标的 transform。
 *
 * 关键设计：box 的逻辑数据（this.box）始终在「世界坐标系」下维护，
 * 与目标节点的 worldTransform 同空间，便于矩阵运算；
 * 而绘制时会换算到 transformer 自身的局部坐标系，避免父级容器变换导致的错位。
 */
export class PixiTransformer extends Container {
  public options: Required<
    Omit<PixiTransformerOptions, 'nodes' | 'boundBoxFunc' | 'onChange' | 'onTransformEnd'>
  > &
    Pick<PixiTransformerOptions, 'boundBoxFunc' | 'onChange' | 'onTransformEnd'>;

  /** 边框图形（多边形 + 旋转把手连线） */
  private readonly border = new Graphics();
  /** 控制点图形集合，按 anchor 名索引，按需创建复用 */
  private readonly anchors = new Map<TransformerAnchor, Graphics>();
  /** 当前绑定的目标节点 */
  private readonly targets: Container[] = [];
  /** 当前拖拽状态，无拖拽时为 null */
  private dragState: DragState | null = null;
  /** 当前 box（世界坐标系），无目标时为 null */
  private box: TransformerBox | null = null;

  /**
   * 构造函数：合并默认配置、初始化边框图形并注册指针事件。
   * @param options 配置项
   */
  public constructor(options: PixiTransformerOptions = {}) {
    super();

    this.options = {
      enabledAnchors: options.enabledAnchors ?? DEFAULT_ANCHORS,
      resizeEnabled: options.resizeEnabled ?? true,
      rotateEnabled: options.rotateEnabled ?? true,
      rotateAnchorOffset: options.rotateAnchorOffset ?? 38,
      padding: options.padding ?? 0,
      anchorSize: options.anchorSize ?? 10,
      anchorCornerRadius: options.anchorCornerRadius ?? 0,
      anchorFill: options.anchorFill ?? 0xffffff,
      anchorStroke: options.anchorStroke ?? 'rgba(0,0,0,0.15)',
      anchorStrokeWidth: options.anchorStrokeWidth ?? 1,
      borderStroke: options.borderStroke ?? 0x6033e1,
      borderStrokeWidth: options.borderStrokeWidth ?? 2,
      keepRatio: options.keepRatio ?? false,
      centeredScaling: options.centeredScaling ?? false,
      flipEnabled: options.flipEnabled ?? true,
      useSingleNodeRotation: options.useSingleNodeRotation ?? true,
      boundBoxFunc: options.boundBoxFunc,
      onChange: options.onChange,
      onTransformEnd: options.onTransformEnd
    };

    this.eventMode = 'static';
    // this.cursor = 'move';
    this.sortableChildren = true;
    this.border.eventMode = 'static';
    // this.border.cursor = 'move';
    this.addChild(this.border);

    // 仅 pointerdown 常驻：移动 / 抬起监听在按下时挂载、抬手时摘除（addDragListeners /
    // removeDragListeners）。否则 globalpointermove 会在空闲时被全局逐帧触发，造成无谓开销。
    // this.on('pointerdown', this.onBorderPointerDown, this);

    this.nodes(options.nodes ?? []);
  }

  /**
   * 读取或设置当前绑定的目标节点。
   * - 不传参：返回当前目标列表的拷贝。
   * - 传参：替换目标列表并立即 update()。
   * @param nodes 新的目标节点列表
   */
  public nodes(nodes?: Container[]): Container[] {
    if (!nodes) {
      return [...this.targets];
    }

    this.targets.length = 0;
    this.targets.push(...nodes);
    this.update();
    return [...this.targets];
  }

  /**
   * 绑定一组目标节点（链式调用），等价于 nodes(nodes)。
   */
  public attachTo(...nodes: Container[]): this {
    this.nodes(nodes);
    return this;
  }

  /**
   * 解绑所有目标（链式调用），框随之隐藏。
   */
  public detach(): this {
    this.nodes([]);
    return this;
  }

  /**
   * 重新计算 box 并重绘。
   * 在目标发生变化、或目标自身 transform 变化后调用，使框与目标保持贴合。
   */
  public update(): this {
    this.box = this.computeBox();
    this.visible = Boolean(this.box);
    this.redraw();
    return this;
  }

  /**
   * 获取当前 box 的拷贝（世界坐标系）。无目标时返回 null。
   */
  public getBox(): TransformerBox | null {
    return this.box ? { ...this.box } : null;
  }

  /**
   * 重置拖拽快照的起始状态为当前状态。
   *
   * 当 `onChange` 回调中修改了目标节点（如文本元素的换行重排导致 bounds 变化），
   * 需要调用此方法更新 dragState 中的 startBox / startContentBox / startWorldMatrices，
   * 避免下一帧指针移动时基于过时的起始状态计算增量，导致 box 跳变。
   *
   * 仅在拖拽进行中（dragState 非空）且 box 存在时生效。
   */
  public refreshDragState(): void {
    if (!this.dragState || !this.box) return;

    for (const target of this.targets) {
      updateTargetTransform(target);
    }

    this.dragState.startBox = { ...this.box };
    this.dragState.startContentBox = contentBoxFromVisualBox(this.box, this.options.padding);
    this.dragState.startWorldMatrices = this.targets.map((target) => getWorldMatrix(target));
    this.dragState.startPositions = this.targets.map((target) => target.position.clone());
  }

  /**
   * 直接设置 box（世界坐标系），跳过 computeBox。
   * 适用于外部需要精确控制框位置的场景。
   * @param box 新的 box，传 null 则隐藏
   */
  public setBox(box: TransformerBox | null): this {
    this.box = box ? { ...box } : null;
    this.visible = Boolean(this.box);
    this.redraw();
    return this;
  }

  /**
   * 销毁变形框：摘除全部事件监听、销毁边框 / 控制点图形并清空引用。
   *
   * 注意：targets 为外部元素节点（归画布 / Element 所有），此处只清空引用、不负责销毁它们。
   * @param options 销毁选项，原样透传给 super.destroy
   */
  public override destroy(options?: DestroyOptions): void {
    // 1. 结束可能进行中的拖拽，摘除全局移动 / 抬起监听
    this.removeDragListeners();
    // 2. 摘除常驻的 pointerdown 监听
    this.off('pointerdown', this.onBorderPointerDown, this);

    // 3. 销毁所有控制点图形（destroy 会连带清理其 pointerdown 监听），并清空集合
    for (const anchor of this.anchors.values()) {
      anchor.destroy();
    }
    this.anchors.clear();

    // 4. 销毁边框图形
    this.border.destroy();

    // 5. 仅清空对外部目标节点 / 运行时状态的引用（targets 不在此销毁）
    this.targets.length = 0;
    this.dragState = null;
    this.box = null;

    super.destroy(options);
  }

  /**
   * 重绘边框与控制点。
   *
   * ⚠️ 坐标换算的关键所在：
   * this.box 处于「世界坐标系」，但本 transformer 作为子节点渲染时，其局部坐标还会
   * 再叠加一次「父级 → stage」的变换（例如 stage 为把原点居中而设置的平移）。
   * 若直接拿世界坐标来绘制，等于把这份父级变换叠加了两次，框就会与目标错位
   * （这正是「框与元素对不上」的根因）。
   *
   * 因此绘制前先把 box 通过 worldToLocal（= transformer 世界矩阵的逆）换算回
   * transformer 自身的局部坐标系；绘制后再叠加父级变换渲染，两者恰好抵消，框与目标贴合。
   */
  private redraw(): void {
    this.border.clear();

    for (const anchor of this.anchors.values()) {
      anchor.visible = false;
    }

    if (!this.box) {
      return;
    }

    // 世界坐标 box → transformer 局部坐标 box（抵消父级变换，避免错位）
    const worldToLocal = invertMatrix(getWorldMatrix(this));
    const box = transformBoxToParent(this.box, worldToLocal);
    const corners = getBoxCorners(box);

    this.border
      .poly(corners.flatMap((p) => [p.x, p.y]))
      .closePath()
      .stroke({ color: this.options.borderStroke, width: this.options.borderStrokeWidth });

    if (this.options.rotateEnabled) {
      // 旋转控制点位于上边线中点的外侧（沿与高度方向相反一侧）
      const topCenter = midpoint(corners[0], corners[1]);
      const rotateDirection = mul(unitY(box.rotation), -Math.sign(box.height || 1));
      const rotatePoint = add(topCenter, mul(rotateDirection, this.options.rotateAnchorOffset));
      this.border
        .moveTo(topCenter.x, topCenter.y)
        .lineTo(rotatePoint.x, rotatePoint.y)
        .stroke({ color: this.options.borderStroke, width: this.options.borderStrokeWidth });
      this.drawAnchor('rotater', rotatePoint, true, box.rotation);
    }

    if (this.options.resizeEnabled) {
      // 八方向缩放控制点：四角取顶点，四边中点取相邻顶点中点
      const positions: Record<TransformerAnchor, Point> = {
        'top-left': corners[0],
        'top-center': midpoint(corners[0], corners[1]),
        'top-right': corners[1],
        'middle-right': midpoint(corners[1], corners[2]),
        'bottom-right': corners[2],
        'bottom-center': midpoint(corners[2], corners[3]),
        'bottom-left': corners[3],
        'middle-left': midpoint(corners[3], corners[0]),
        rotater: new Point()
      };

      for (const anchor of this.options.enabledAnchors) {
        if (anchor !== 'rotater') {
          this.drawAnchor(anchor, positions[anchor], false, box.rotation);
        }
      }
    }
  }

  /**
   * 绘制（按需创建并复用）单个控制点，并定位到指定位置。
   *
   * 样式跟随 Figma 设计规范：
   * - 四角控制点为圆形（白色填充 + 半透明黑描边）
   * - 四边中点为胶囊形（白色填充 + 半透明黑描边）
   * - 旋转控制点保持圆形
   *
   * @param anchorName 控制点标识
   * @param position 控制点中心位置（已换算到 transformer 局部坐标系）
   * @param round 是否绘制为圆形（旋转控制点为圆）
   * @param rotation 控制点自身旋转角度（与局部 box 旋转一致，使方形控制点贴合边框朝向）
   */
  private drawAnchor(
    anchorName: TransformerAnchor,
    position: Point,
    round: boolean,
    rotation: number
  ): void {
    let anchor = this.anchors.get(anchorName);

    // 懒创建：首次使用某控制点时才创建图形并绑定按下事件
    if (!anchor) {
      anchor = new Graphics();
      anchor.eventMode = 'static';
      anchor.cursor = CURSORS[anchorName];
      anchor.zIndex = 1;
      anchor.on('pointerdown', (event) => this.onAnchorPointerDown(event, anchorName));
      this.anchors.set(anchorName, anchor);
      this.addChild(anchor);
    }

    const size = this.options.anchorSize;
    anchor.clear();

    if (round) {
      // 旋转控制点：圆形
      anchor
        .circle(0, 0, size * 0.55)
        .fill(this.options.anchorFill)
        .stroke({ color: this.options.anchorStroke, width: this.options.anchorStrokeWidth });
    } else if (isCornerAnchor(anchorName)) {
      // 四角控制点：圆形（Figma 规范：白色填充、半径 = size / 2）
      anchor
        .circle(0, 0, size / 2)
        .fill(this.options.anchorFill)
        .stroke({ color: this.options.anchorStroke, width: this.options.anchorStrokeWidth });
    } else if (isHorizontalEdgeAnchor(anchorName)) {
      // 上下边中点：水平胶囊形（Figma 规范：18×6 胶囊）
      anchor
        .roundRect(-size * 0.75, -size * 0.25, size * 1.5, size * 0.5, size * 0.25)
        .fill(this.options.anchorFill)
        .stroke({ color: this.options.anchorStroke, width: this.options.anchorStrokeWidth });
    } else {
      // 左右边中点：垂直胶囊形（Figma 规范：6×18 胶囊）
      anchor
        .roundRect(-size * 0.25, -size * 0.75, size * 0.5, size * 1.5, size * 0.25)
        .fill(this.options.anchorFill)
        .stroke({ color: this.options.anchorStroke, width: this.options.anchorStrokeWidth });
    }

    anchor.position.copyFrom(position);
    anchor.rotation = rotation;
    anchor.visible = true;
  }

  /**
   * 进入拖拽时挂载全局移动 / 抬起监听。仅在 pointerdown 之后生效，
   * 使拖拽过程能持续捕获指针（即便移出元素范围）。
   */
  private addDragListeners(): void {
    this.on('globalpointermove', this.onGlobalPointerMove, this);
    this.on('pointerup', this.onGlobalPointerUp, this);
    this.on('pointerupoutside', this.onGlobalPointerUp, this);
    this.on('globalpointerup', this.onGlobalPointerUp, this);
    this.on('globalpointerupoutside', this.onGlobalPointerUp, this);
  }

  /**
   * 结束拖拽时摘除全局移动 / 抬起监听，恢复到「只监听 pointerdown」的空闲态，
   * 避免空闲时 globalpointermove 仍被全局逐帧触发。
   */
  private removeDragListeners(): void {
    this.off('globalpointermove', this.onGlobalPointerMove, this);
    this.off('pointerup', this.onGlobalPointerUp, this);
    this.off('pointerupoutside', this.onGlobalPointerUp, this);
    this.off('globalpointerup', this.onGlobalPointerUp, this);
    this.off('globalpointerupoutside', this.onGlobalPointerUp, this);
  }

  /**
   * 点击边框本体（非控制点）时触发：进入「移动」拖拽。
   */
  private onBorderPointerDown(event: FederatedPointerEvent): void {
    if (event.target !== this.border && event.target !== this) {
      return;
    }

    const box = this.box;
    if (!box || this.targets.length === 0) {
      return;
    }

    event.stopPropagation();
    this.dragState = this.createDragState('move', event.global);
    this.addDragListeners();
  }

  /**
   * 点击某个控制点时触发：进入「旋转」或「缩放」拖拽。
   * @param event 指针事件
   * @param anchor 被按下的控制点
   */
  private onAnchorPointerDown(event: FederatedPointerEvent, anchor: TransformerAnchor): void {
    const box = this.box;
    if (!box || this.targets.length === 0) {
      return;
    }

    event.stopPropagation();
    this.dragState = this.createDragState(
      anchor === 'rotater' ? 'rotate' : 'resize',
      event.global,
      anchor
    );
    this.addDragListeners();
  }

  /**
   * 指针移动（全局监听，拖出元素仍生效）时：根据当前模式计算下一帧 box 并应用到目标。
   * 若主键已松开则提前结束拖拽。
   */
  private onGlobalPointerMove(event: FederatedPointerEvent): void {
    if (!this.dragState) {
      return;
    }

    if (isPrimaryButtonReleased(event)) {
      this.onGlobalPointerUp();
      return;
    }

    const state = this.dragState;
    let nextBox: TransformerBox;

    if (state.mode === 'move') {
      // 移动：直接按指针位移平移 box（世界坐标）
      const dx = event.global.x - state.pointerStart.x;
      const dy = event.global.y - state.pointerStart.y;
      nextBox = { ...state.startBox, x: state.startBox.x + dx, y: state.startBox.y + dy };
    } else if (state.mode === 'rotate') {
      nextBox = this.getRotatedBox(state, event.global);
    } else {
      nextBox = this.getResizedBox(state, event.global);
    }

    // 允许外部通过 boundBoxFunc 约束最终 box
    const bounded = this.options.boundBoxFunc?.(state.startBox, nextBox) ?? nextBox;
    this.applyBoxTransform(state, bounded);
    this.box = bounded;
    this.redraw();
    this.options.onChange?.([...this.targets], state.mode);
  }

  /**
   * 指针抬起（含离开画布）：结束当前拖拽，并触发 onTransformEnd 回调。
   */
  private onGlobalPointerUp(): void {
    if (!this.dragState) {
      return;
    }

    this.removeDragListeners();
    this.dragState = null;
    this.redraw();
    this.options.onTransformEnd?.([...this.targets]);
  }

  /**
   * 构造一次拖拽的快照状态：刷新各目标局部变换、记录起始 box / 世界矩阵 / 位置。
   * @param mode 拖拽模式
   * @param pointerStart 指针按下时的世界坐标
   * @param anchor 触发的控制点（移动模式无）
   */
  private createDragState(
    mode: DragMode,
    pointerStart: Point,
    anchor?: TransformerAnchor
  ): DragState {
    for (const target of this.targets) {
      updateTargetTransform(target);
    }

    return {
      mode,
      anchor,
      pointerStart: pointerStart.clone(),
      startBox: { ...this.box! },
      startContentBox: contentBoxFromVisualBox(this.box!, this.options.padding),
      startWorldMatrices: this.targets.map((target) => getWorldMatrix(target)),
      startPositions: this.targets.map((target) => target.position.clone())
    };
  }

  /**
   * 旋转模式：根据指针相对中心的角度差，计算旋转后的 box（中心与尺寸不变，旋转角累加）。
   * @param state 拖拽快照
   * @param pointer 当前指针世界坐标
   */
  private getRotatedBox(state: DragState, pointer: Point): TransformerBox {
    const box = state.startBox;
    const center = getBoxCenter(box);
    const startAngle = Math.atan2(state.pointerStart.y - center.y, state.pointerStart.x - center.x);
    const angle = Math.atan2(pointer.y - center.y, pointer.x - center.x);
    return boxFromCenter(center, box.width, box.height, box.rotation + angle - startAngle);
  }

  /**
   * 缩放模式：根据拖动的控制点与指针位置，计算缩放后的 box。
   * 支持中心缩放（centeredScaling）、锁定宽高比（keepRatio）、禁止翻转（flipEnabled）。
   * @param state 拖拽快照
   * @param pointer 当前指针世界坐标
   */
  private getResizedBox(state: DragState, pointer: Point): TransformerBox {
    const box = state.startBox;
    const anchor = state.anchor!;
    const xAxis = unitX(box.rotation);
    const yAxis = unitY(box.rotation);
    const horizontal = anchor.includes('left') || anchor.includes('right');
    const vertical = anchor.includes('top') || anchor.includes('bottom');
    let width: number;
    let height: number;

    if (this.options.centeredScaling) {
      // 中心缩放：以中心为基准，指针在两轴上的投影 ×2 即为新尺寸
      const center = getBoxCenter(box);
      const rel = sub(pointer, center);
      const projectedWidth = dot(rel, xAxis) * 2;
      const projectedHeight = dot(rel, yAxis) * 2;

      if (anchor.includes('left')) {
        width = -projectedWidth;
      } else if (anchor.includes('right')) {
        width = projectedWidth;
      } else {
        width = box.width;
      }

      if (anchor.includes('top')) {
        height = -projectedHeight;
      } else if (anchor.includes('bottom')) {
        height = projectedHeight;
      } else {
        height = box.height;
      }
    } else {
      // 普通缩放：以对侧顶点（固定点）为基准，指针在两轴上的投影即为新尺寸
      const fixed = this.getFixedPoint(box, anchor);
      const rel = sub(pointer, fixed);
      width = dot(rel, xAxis);
      height = dot(rel, yAxis);

      if (anchor.includes('left')) {
        width = -width;
      } else if (!horizontal) {
        width = box.width;
      }

      if (anchor.includes('top')) {
        height = -height;
      } else if (!vertical) {
        height = box.height;
      }
    }

    // 锁定宽高比：以主导轴为准调整另一轴
    if (this.options.keepRatio && horizontal && vertical && Math.abs(box.height) > EPSILON) {
      const ratio = Math.abs(box.width / box.height);
      if (Math.abs(width / height) > ratio) {
        height = Math.sign(height || 1) * Math.abs(width / ratio);
      } else {
        width = Math.sign(width || 1) * Math.abs(height * ratio);
      }
    }

    // 禁止翻转：尺寸钳制为正
    if (!this.options.flipEnabled) {
      width = Math.max(EPSILON, width);
      height = Math.max(EPSILON, height);
    }

    return this.options.centeredScaling
      ? boxFromCenter(getBoxCenter(box), width, height, box.rotation)
      : this.boxFromFixedPoint(box, anchor, this.getFixedPoint(box, anchor), width, height);
  }

  /**
   * 取缩放时保持不动的「固定点」（即被拖动控制点的对侧点）。
   * 中心缩放时固定点恒为中心。
   * @param box 起始 box
   * @param anchor 被拖动的控制点
   */
  private getFixedPoint(box: TransformerBox, anchor: TransformerAnchor): Point {
    const corners = getBoxCorners(box);

    if (this.options.centeredScaling) {
      return getBoxCenter(box);
    }

    switch (anchor) {
      case 'top-left':
        return corners[2];
      case 'top-center':
        return midpoint(corners[2], corners[3]);
      case 'top-right':
        return corners[3];
      case 'middle-right':
        return midpoint(corners[3], corners[0]);
      case 'bottom-right':
        return corners[0];
      case 'bottom-center':
        return midpoint(corners[0], corners[1]);
      case 'bottom-left':
        return corners[1];
      case 'middle-left':
        return midpoint(corners[1], corners[2]);
      default:
        return getBoxCenter(box);
    }
  }

  /**
   * 已知固定点与新尺寸，反算 box 左上角坐标（沿两轴反向投影）。
   * fixedCoords 给出固定点在 box 宽高方向的归一化坐标（0~1）。
   * @param box 起始 box（提供 rotation）
   * @param anchor 被拖动的控制点
   * @param fixed 固定点坐标
   * @param width 新宽度
   * @param height 新高度
   */
  private boxFromFixedPoint(
    box: TransformerBox,
    anchor: TransformerAnchor,
    fixed: Point,
    width: number,
    height: number
  ): TransformerBox {
    const xAxis = unitX(box.rotation);
    const yAxis = unitY(box.rotation);

    if (this.options.centeredScaling) {
      return {
        ...box,
        x: fixed.x - (xAxis.x * width + yAxis.x * height) / 2,
        y: fixed.y - (xAxis.y * width + yAxis.y * height) / 2,
        width,
        height
      };
    }

    // 固定点在宽 / 高方向上的归一化坐标（fx, fy）
    const fixedCoords: Record<TransformerAnchor, [number, number]> = {
      'top-left': [1, 1],
      'top-center': [0.5, 1],
      'top-right': [0, 1],
      'middle-right': [0, 0.5],
      'bottom-right': [0, 0],
      'bottom-center': [0.5, 0],
      'bottom-left': [1, 0],
      'middle-left': [1, 0.5],
      rotater: [0.5, 0.5]
    };
    const [fx, fy] = fixedCoords[anchor];

    return {
      ...box,
      x: fixed.x - xAxis.x * width * fx - yAxis.x * height * fy,
      y: fixed.y - xAxis.y * width * fx - yAxis.y * height * fy,
      width,
      height
    };
  }

  /**
   * 把「box 从 startBox 变到 nextBox」这一变换反向应用到各目标节点。
   * - move：按位移平移每个目标（在每个目标父级坐标系中增量，兼容不同父容器）
   * - rotate：绕 box 中心旋转，并相应平移目标原点
   * - resize：用 box 矩阵的增量左乘各目标的起始世界矩阵
   * @param state 拖拽快照
   * @param nextBox 目标 box（世界坐标）
   */
  private applyBoxTransform(state: DragState, nextBox: TransformerBox): void {
    if (state.mode === 'move') {
      const dx = nextBox.x - state.startBox.x;
      const dy = nextBox.y - state.startBox.y;

      // 将世界坐标的位移换算到每个目标各自父级的坐标系，再叠加到起始位置，
      // 这样即使各目标处于不同父容器也能正确跟随。
      this.targets.forEach((target, index) => {
        const parentWorld = target.parent?.worldTransform ?? new Matrix();
        const localStart = parentWorld.applyInverse(state.pointerStart);
        const localEnd = parentWorld.applyInverse(
          new Point(state.pointerStart.x + dx, state.pointerStart.y + dy)
        );

        target.position.set(
          state.startPositions[index].x + localEnd.x - localStart.x,
          state.startPositions[index].y + localEnd.y - localStart.y
        );
        target.updateLocalTransform();
      });
      return;
    }

    if (state.mode === 'rotate') {
      const deltaRotation = nextBox.rotation - state.startBox.rotation;
      const center = getBoxCenter(state.startBox);

      // 每个目标：旋转其线性部分，并把原点绕中心做相同旋转
      this.targets.forEach((target, index) => {
        const startWorld = state.startWorldMatrices[index];
        const targetOrigin = new Point(startWorld.tx, startWorld.ty);
        const rotatedOrigin = rotatePoint(targetOrigin, center, deltaRotation);
        const world = rotateMatrixLinear(startWorld, deltaRotation);
        world.tx = rotatedOrigin.x;
        world.ty = rotatedOrigin.y;
        setWorldMatrix(target, world);
      });
      return;
    }

    // resize：内容 box（去 padding）的矩阵增量 × 起始世界矩阵，得到目标新世界矩阵
    const contentNextBox = contentBoxFromVisualBox(nextBox, this.options.padding);
    const delta = multiplyMatrices(
      boxToMatrix(contentNextBox),
      invertMatrix(boxToMatrix(state.startContentBox))
    );

    this.targets.forEach((target, index) => {
      const world = multiplyMatrices(delta, state.startWorldMatrices[index]);
      setWorldMatrix(target, world);
    });
  }

  /**
   * 计算当前所有目标在「世界坐标系」下的包围 box。
   * - 单目标且 useSingleNodeRotation：沿用目标自身的旋转（getSingleTargetBox）
   * - 多目标：取所有目标轴对齐包围盒（rotation 固定为 0）
   * 无目标时返回 null。
   * @param padding 与目标之间的内边距
   */
  private computeBox(padding = this.options.padding): TransformerBox | null {
    if (this.targets.length === 0) {
      return null;
    }

    const rects = this.targets.map((target) => getWorldRect(target));
    const minX = Math.min(...rects.map((rect) => rect.x));
    const minY = Math.min(...rects.map((rect) => rect.y));
    const maxX = Math.max(...rects.map((rect) => rect.x + rect.width));
    const maxY = Math.max(...rects.map((rect) => rect.y + rect.height));
    if (this.targets.length === 1 && this.options.useSingleNodeRotation) {
      return getSingleTargetBox(this.targets[0], padding);
    }

    return {
      x: minX - padding,
      y: minY - padding,
      width: maxX - minX + padding * 2,
      height: maxY - minY + padding * 2,
      rotation: 0
    };
  }
}

/**
 * 计算单个目标在「世界坐标系」下、保留其自身旋转的 box。
 * 取目标局部包围盒的左上、右上、左下三个角，经世界矩阵变换后反推原点 / 宽 / 高 / 旋转。
 * @param target 目标节点
 * @param padding 内边距
 */
function getSingleTargetBox(target: Container, padding: number): TransformerBox {
  updateTargetTransform(target);

  const local = getLocalRect(target);
  const world = getWorldMatrix(target);
  const topLeft = world.apply(new Point(local.x, local.y));
  const topRight = world.apply(new Point(local.x + local.width, local.y));
  const bottomLeft = world.apply(new Point(local.x, local.y + local.height));
  const widthVector = sub(topRight, topLeft);
  const heightVector = sub(bottomLeft, topLeft);
  const width = Math.hypot(widthVector.x, widthVector.y);
  const rotation = Math.atan2(widthVector.y, widthVector.x);
  const xAxis = unitX(rotation);
  const yAxis = unitY(rotation);
  const signedHeight = dot(heightVector, yAxis);
  const heightSign = Math.sign(signedHeight || 1);

  return {
    x: topLeft.x - xAxis.x * padding - yAxis.x * padding * heightSign,
    y: topLeft.y - xAxis.y * padding - yAxis.y * padding * heightSign,
    width: width + padding * 2,
    height: signedHeight + heightSign * padding * 2,
    rotation
  };
}

/**
 * 取目标在「自身局部坐标系」下的包围矩形（兼容 Rectangle 与 Bounds 两种字段命名）。
 */
function getLocalRect(target: Container): Rectangle {
  const bounds = target.getLocalBounds() as unknown as Rectangle & {
    minX?: number;
    minY?: number;
    maxX?: number;
    maxY?: number;
  };

  if (typeof bounds.x === 'number') {
    return new Rectangle(bounds.x, bounds.y, bounds.width, bounds.height);
  }

  return new Rectangle(
    bounds.minX ?? 0,
    bounds.minY ?? 0,
    (bounds.maxX ?? 0) - (bounds.minX ?? 0),
    (bounds.maxY ?? 0) - (bounds.minY ?? 0)
  );
}

/**
 * 取目标在「世界坐标系」下的轴对齐包围矩形（忽略旋转，取四角的最小/最大值）。
 */
function getWorldRect(target: Container): Rectangle {
  const local = getLocalRect(target);
  const world = getWorldMatrix(target);
  const points = [
    world.apply(new Point(local.x, local.y)),
    world.apply(new Point(local.x + local.width, local.y)),
    world.apply(new Point(local.x + local.width, local.y + local.height)),
    world.apply(new Point(local.x, local.y + local.height))
  ];
  const minX = Math.min(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxX = Math.max(...points.map((point) => point.x));
  const maxY = Math.max(...points.map((point) => point.y));

  return new Rectangle(minX, minY, maxX - minX, maxY - minY);
}

/** 刷新目标的局部变换矩阵（同步 position/scale/rotation 到 localTransform）。 */
function updateTargetTransform(target: Container): void {
  target.updateLocalTransform();
}

/**
 * 计算目标的「世界变换矩阵」：自顶向下（从根 stage）逐级左乘各祖先的 localTransform。
 * 之所以手动遍历而非直接读 worldTransform，是为了保证取到的是最新值
 * （PixiJS 的 worldTransform 在渲染前可能未刷新）。
 */
function getWorldMatrix(target: Container): Matrix {
  const chain: Container[] = [];
  let current: Container | null = target;

  while (current) {
    chain.unshift(current);
    current = current.parent;
  }

  return chain.reduce((matrix, container) => {
    container.updateLocalTransform();
    return multiplyMatrices(matrix, container.localTransform);
  }, new Matrix());
}

/**
 * 反向设置目标的「世界变换矩阵」：先换算到目标自身局部坐标，再分解为 position/scale/rotation。
 * @param target 目标节点
 * @param world 期望的世界矩阵
 */
function setWorldMatrix(target: Container, world: Matrix): void {
  const parentWorld = target.parent ? getWorldMatrix(target.parent) : new Matrix();
  const local = multiplyMatrices(invertMatrix(parentWorld), world);
  applyLocalMatrix(target, local);
}

/**
 * 把一个局部变换矩阵分解并应用到目标的 position / scale / rotation。
 * 优先使用目标自带的 setFromMatrix（如有），否则手动分解。
 */
function applyLocalMatrix(target: Container, matrix: Matrix): void {
  const targetWithSetter = target as Container & {
    setFromMatrix?: (matrix: Matrix) => void;
  };

  if (targetWithSetter.setFromMatrix) {
    targetWithSetter.setFromMatrix(matrix);
    return;
  }

  // 分解：缩放取列向量长度，旋转取 a/b 的反正切，平移取 tx/ty
  const scaleX = Math.hypot(matrix.a, matrix.b);
  const det = matrix.a * matrix.d - matrix.b * matrix.c;
  const scaleY = scaleX > EPSILON ? det / scaleX : Math.hypot(matrix.c, matrix.d);
  const rotation = Math.atan2(matrix.b, matrix.a);

  target.position.set(matrix.tx, matrix.ty);
  target.scale.set(scaleX, scaleY);
  target.rotation = rotation;
}

/**
 * 仅旋转矩阵的「线性部分」（a/b/c/d），平移（tx/ty）保持不变。
 * 用于旋转模式下旋转目标的线性部分，原点另行单独旋转处理。
 */
function rotateMatrixLinear(matrix: Matrix, rotation: number): Matrix {
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  return new Matrix(
    cos * matrix.a - sin * matrix.b,
    sin * matrix.a + cos * matrix.b,
    cos * matrix.c - sin * matrix.d,
    sin * matrix.c + cos * matrix.d,
    matrix.tx,
    matrix.ty
  );
}

/**
 * 把 box 转换为一个变换矩阵：以 (x,y) 为原点，宽沿 box.x 轴、高沿 box.y 轴。
 * 用于 resize 模式下把 box 表达为矩阵参与增量运算。
 */
function boxToMatrix(box: TransformerBox): Matrix {
  const cos = Math.cos(box.rotation);
  const sin = Math.sin(box.rotation);
  return new Matrix(
    cos * box.width,
    sin * box.width,
    -sin * box.height,
    cos * box.height,
    box.x,
    box.y
  );
}

/**
 * 由「带 padding 的可视 box」反推「不含 padding 的内容 box」。
 * padding 为 0 时直接返回原 box。
 */
function contentBoxFromVisualBox(box: TransformerBox, padding: number): TransformerBox {
  if (padding === 0) {
    return box;
  }

  const xAxis = unitX(box.rotation);
  const yAxis = unitY(box.rotation);
  const widthSign = Math.sign(box.width || 1);
  const heightSign = Math.sign(box.height || 1);

  return {
    x: box.x + xAxis.x * padding * widthSign + yAxis.x * padding * heightSign,
    y: box.y + xAxis.y * padding * widthSign + yAxis.y * padding * heightSign,
    width: box.width - padding * 2 * widthSign,
    height: box.height - padding * 2 * heightSign,
    rotation: box.rotation
  };
}

/** 矩阵乘法 left × right（注意 PixiJS Matrix 列主序的 a/b/c/d/tx/ty 约定）。 */
function multiplyMatrices(left: Matrix, right: Matrix): Matrix {
  return new Matrix(
    left.a * right.a + left.c * right.b,
    left.b * right.a + left.d * right.b,
    left.a * right.c + left.c * right.d,
    left.b * right.c + left.d * right.d,
    left.a * right.tx + left.c * right.ty + left.tx,
    left.b * right.tx + left.d * right.ty + left.ty
  );
}

/** 矩阵求逆；行列式过小时返回单位矩阵，避免除零。 */
function invertMatrix(matrix: Matrix): Matrix {
  const det = matrix.a * matrix.d - matrix.b * matrix.c;
  if (Math.abs(det) < EPSILON) {
    return new Matrix();
  }

  const id = 1 / det;
  return new Matrix(
    matrix.d * id,
    -matrix.b * id,
    -matrix.c * id,
    matrix.a * id,
    (matrix.c * matrix.ty - matrix.d * matrix.tx) * id,
    (matrix.b * matrix.tx - matrix.a * matrix.ty) * id
  );
}

/**
 * 取 box 的四个角点（按 左上、右上、右下、左下 顺序）。
 */
function getBoxCorners(box: TransformerBox): [Point, Point, Point, Point] {
  const xAxis = unitX(box.rotation);
  const yAxis = unitY(box.rotation);
  const topLeft = new Point(box.x, box.y);
  const topRight = add(topLeft, mul(xAxis, box.width));
  const bottomRight = add(topRight, mul(yAxis, box.height));
  const bottomLeft = add(topLeft, mul(yAxis, box.height));
  return [topLeft, topRight, bottomRight, bottomLeft];
}

/** 取 box 的中心点（对角线中点）。 */
function getBoxCenter(box: TransformerBox): Point {
  const corners = getBoxCorners(box);
  return midpoint(corners[0], corners[2]);
}

/** 由中心点 + 宽高 + 旋转，反推左上角坐标，构造 box。 */
function boxFromCenter(
  center: Point,
  width: number,
  height: number,
  rotation: number
): TransformerBox {
  const xAxis = unitX(rotation);
  const yAxis = unitY(rotation);

  return {
    x: center.x - (xAxis.x * width + yAxis.x * height) / 2,
    y: center.y - (xAxis.y * width + yAxis.y * height) / 2,
    width,
    height,
    rotation
  };
}

/**
 * 把一个 box 通过给定的 worldToLocal 矩阵从「世界坐标系」转换到另一坐标系（如 transformer 局部）。
 * 做法：转换 3 个角点后重建（原点 + 宽 / 高向量），可正确保留旋转与翻转（宽高带符号）。
 * @param box 源 box（世界坐标）
 * @param worldToLocal 世界坐标 → 目标坐标系的矩阵
 */
function transformBoxToParent(box: TransformerBox, worldToLocal: Matrix): TransformerBox {
  const corners = getBoxCorners(box);
  const topLeft = worldToLocal.apply(corners[0]);
  const topRight = worldToLocal.apply(corners[1]);
  const bottomLeft = worldToLocal.apply(corners[3]);
  const widthVector = sub(topRight, topLeft);
  const heightVector = sub(bottomLeft, topLeft);
  const rotation = Math.atan2(widthVector.y, widthVector.x);
  const xAxis = unitX(rotation);
  const yAxis = unitY(rotation);

  return {
    x: topLeft.x,
    y: topLeft.y,
    width: dot(widthVector, xAxis),
    height: dot(heightVector, yAxis),
    rotation
  };
}

/** 取 box 旋转后的 x 轴单位向量（cos, sin）。 */
function unitX(rotation: number): Point {
  return new Point(Math.cos(rotation), Math.sin(rotation));
}

/** 取 box 旋转后的 y 轴单位向量（-sin, cos）。 */
function unitY(rotation: number): Point {
  return new Point(-Math.sin(rotation), Math.cos(rotation));
}

/** 两点中点。 */
function midpoint(a: Point, b: Point): Point {
  return new Point((a.x + b.x) / 2, (a.y + b.y) / 2);
}

/** 向量加法。 */
function add(a: Point, b: Point): Point {
  return new Point(a.x + b.x, a.y + b.y);
}

/** 向量减法。 */
function sub(a: Point, b: Point): Point {
  return new Point(a.x - b.x, a.y - b.y);
}

/** 向量数乘。 */
function mul(point: Point, value: number): Point {
  return new Point(point.x * value, point.y * value);
}

/** 向量归一化；长度过小时返回 (0,1) 以避免除零。（当前未使用，保留备用） */
// function normalize(point: Point): Point {
//   const length = Math.hypot(point.x, point.y);
//   if (length < EPSILON) {
//     return new Point(0, 1);
//   }

//   return new Point(point.x / length, point.y / length);
// }

/** 向量点积。 */
function dot(a: Point, b: Point): number {
  return a.x * b.x + a.y * b.y;
}

/** 将点绕中心旋转指定角度。 */
function rotatePoint(point: Point, center: Point, rotation: number): Point {
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return new Point(center.x + dx * cos - dy * sin, center.y + dx * sin + dy * cos);
}

/** 判断是否为四角控制点（top-left、top-right、bottom-left、bottom-right）。 */
function isCornerAnchor(anchor: TransformerAnchor): boolean {
  return (
    anchor === 'top-left' ||
    anchor === 'top-right' ||
    anchor === 'bottom-left' ||
    anchor === 'bottom-right'
  );
}

/** 判断是否为上下边中点控制点（top-center、bottom-center），对应水平胶囊形。 */
function isHorizontalEdgeAnchor(anchor: TransformerAnchor): boolean {
  return anchor === 'top-center' || anchor === 'bottom-center';
}

/**
 * 判断鼠标主键是否已松开（用于拖拽中检测鼠标在画布外被释放的情况）。
 * buttons === 0 表示没有任何按键按下。
 */
function isPrimaryButtonReleased(event: FederatedPointerEvent): boolean {
  const eventWithButtons = event as FederatedPointerEvent & { buttons?: number };
  return typeof eventWithButtons.buttons === 'number' && eventWithButtons.buttons === 0;
}
